import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import reportTemplatesAPI from '@/api/report-templates';
import { fetchAllPages } from '@/api/pagination';
import { TENANT_SWITCH_FULFILLED } from '@/features/auth/tenantSwitchActionType';
import { normalizeData } from '@/utils/jsonapi';

/**
 * Entity adapter for normalized report template state management
 */
const reportTemplatesAdapter = createEntityAdapter({
  selectId: (template) => template.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Fetch the complete template catalog unless an explicit page is requested.
 */
export const fetchReportTemplatesThunk = createAsyncThunk(
  'reportTemplates/fetchAll',
  async (params = {}, { rejectWithValue, getState, signal }) => {
    try {
      // All pages belong to the organization that started this request.
      const tenantId = getState().auth?.currentTenantId;
      const requestOptions = { signal, ...(tenantId ? { headers: { 'X-Tenant-ID': tenantId } } : {}) };
      const jsonApiResponse = params.page != null
        ? await reportTemplatesAPI.getAll(params, requestOptions)
        : await fetchAllPages(reportTemplatesAPI.getAll, params, requestOptions);
      return {
        data: normalizeData(jsonApiResponse.data || jsonApiResponse.results || []),
        meta: jsonApiResponse.meta || { pagination: { count: jsonApiResponse.count } },
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0]?.detail ||
        error.apiError?.message ||
        error.message ||
        'Failed to load report templates';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to fetch a single report template by ID
 */
export const fetchReportTemplateByIdThunk = createAsyncThunk(
  'reportTemplates/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportTemplatesAPI.getById(id);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0]?.detail ||
        error.message ||
        'Failed to load report template';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to create a new report template
 */
export const createReportTemplateThunk = createAsyncThunk(
  'reportTemplates/create',
  async (templateData, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportTemplatesAPI.create(templateData);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0]?.detail ||
        error.message ||
        'Failed to create report template';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to update an existing report template
 */
export const updateReportTemplateThunk = createAsyncThunk(
  'reportTemplates/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await reportTemplatesAPI.update(id, data);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0]?.detail ||
        error.message ||
        'Failed to update report template';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to delete a report template
 */
export const deleteReportTemplateThunk = createAsyncThunk(
  'reportTemplates/delete',
  async (id, { rejectWithValue }) => {
    try {
      await reportTemplatesAPI.delete(id);
      return id;
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0]?.detail ||
        error.message ||
        'Failed to delete report template';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Report Templates slice
 */
const reportTemplatesSlice = createSlice({
  name: 'reportTemplates',
  initialState: reportTemplatesAdapter.getInitialState({
    currentTemplate: null,
    catalogRequestId: null,
    loading: false,
    error: null,
    pagination: null,
  }),
  reducers: {
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload;
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchReportTemplatesThunk.pending, (state, action) => {
        state.catalogRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportTemplatesThunk.fulfilled, (state, action) => {
        if (state.catalogRequestId !== action.meta.requestId) return;
        state.catalogRequestId = null;
        state.loading = false;
        reportTemplatesAdapter.setAll(state, action.payload?.data || []);
        state.pagination = action.payload?.meta?.pagination || null;
      })
      .addCase(fetchReportTemplatesThunk.rejected, (state, action) => {
        if (state.catalogRequestId !== action.meta.requestId) return;
        state.catalogRequestId = null;
        state.loading = false;
        state.error = action.meta.aborted ? null : action.payload;
      })
      // Fetch by ID
      .addCase(fetchReportTemplateByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportTemplateByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportTemplatesAdapter.upsertOne(state, action.payload);
        state.currentTemplate = action.payload;
      })
      .addCase(fetchReportTemplateByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createReportTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReportTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportTemplatesAdapter.addOne(state, action.payload);
        state.currentTemplate = action.payload;
      })
      .addCase(createReportTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateReportTemplateThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReportTemplateThunk.fulfilled, (state, action) => {
        state.loading = false;
        reportTemplatesAdapter.upsertOne(state, action.payload);
        if (state.currentTemplate?.id === action.payload.id) {
          state.currentTemplate = action.payload;
        }
      })
      .addCase(updateReportTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteReportTemplateThunk.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        reportTemplatesAdapter.removeOne(state, action.meta.arg);
      })
      .addCase(deleteReportTemplateThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteReportTemplateThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(TENANT_SWITCH_FULFILLED, (state) => {
        reportTemplatesAdapter.removeAll(state);
        state.catalogRequestId = null;
        state.currentTemplate = null;
        state.pagination = null;
        state.loading = false;
        state.error = null;
      });
  },
});

export const { setCurrentTemplate, clearCurrentTemplate, clearError } =
  reportTemplatesSlice.actions;

// Adapter selectors
export const {
  selectAll: selectAllReportTemplates,
  selectById: selectReportTemplateById,
} = reportTemplatesAdapter.getSelectors(
  (state) => state.reportTemplates || reportTemplatesAdapter.getInitialState()
);

// Additional selectors
export const selectCurrentReportTemplate = (state) =>
  state.reportTemplates?.currentTemplate || null;
export const selectReportTemplatesLoading = (state) =>
  state.reportTemplates?.loading || false;
export const selectReportTemplatesError = (state) =>
  state.reportTemplates?.error || null;

export default reportTemplatesSlice.reducer;
